import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  styled,
  Grid,
  DialogActions,
  Button,
} from "@mui/material";
import { Field, Form, Formik } from "formik";
import { useParams } from "react-router-dom";

import { useURMAContext } from "../../../context";
import { APIURMA } from "../../../api";
import { ICreateURMAUserData } from "../../../api/urma/index.types";
import { TTypeMessage } from "../../ui/Snackbar";
import { FormInput, FormSelect, FormCheckbox, Snackbar } from "../../ui";
import { IApiError } from "../../../constants";

import {
  SUBSCRIBER_TYPE_OPTIONS,
  LOGIN_CLASSES_OPTIONS,
  LOGIN_DOMAIN_OPTIONS,
  URMA_USER_SCHEMA,
  URMA_USER_INITIAL_STATE_FORM,
} from "../constants";

const StyledFrom = styled(Form)({ marginTop: 10 });

interface IProps {
  open: boolean;
  setOpen: (isOpen: boolean) => void;
}
const CreateURMAUserModal = ({ open, setOpen }: IProps): React.ReactElement => {
  const params: { id: string } = useParams();
  const id = +params.id;
  const { urmaProfile, setUrmaUser } = useURMAContext();
  const [isFetching, setFetching] = useState(false);
  const [snackBarState, setSnackBarState] = useState({
    open: false,
    resultType: "" as TTypeMessage,
    responseSuccessMessage: "Created",
    responseSuccessErrorMessage: "",
  });
  const handleCloseSnackBar = () => {
    setSnackBarState({ ...snackBarState, open: false });
  };
  const handleClose = () => setOpen(false);
  const handleSubmit = async (values: ICreateURMAUserData) => {
    try {
      setFetching(true);
      const userResponse = await APIURMA.createURMAUserRequest(values);
      setUrmaUser(userResponse.data);
      setSnackBarState({
        ...snackBarState,
        resultType: "success",
        open: true,
      });
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (error) {
      setSnackBarState({
        ...snackBarState,
        resultType: "error",
        responseSuccessErrorMessage: (error as IApiError).response.data.message || "",
        open: true,
      });
    } finally {
      setFetching(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} aria-labelledby="dialog-create-user">
      <DialogTitle>Create user</DialogTitle>
      <DialogContent>
        <Formik
          initialValues={{
            ...URMA_USER_INITIAL_STATE_FORM,
            profile_id: urmaProfile.profile_id,
            account_id: id,
          }}
          onSubmit={handleSubmit}
          validationSchema={URMA_USER_SCHEMA}
          validateOnChange
        >
          {({ isValid }) => (
            <StyledFrom>
              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <Field name="name" label="Name" placeholder="Name" component={FormInput} />
                </Grid>
                <Grid item xs={4}>
                  <Field
                    name="subscriber_type"
                    label="Subscriber type"
                    placeholder="Subscriber type"
                    component={FormSelect}
                    options={SUBSCRIBER_TYPE_OPTIONS}
                    contextualHelpText="Subscriber type of a user"
                  />
                </Grid>
                <Grid item xs={4}>
                  <Field
                    name="login_class"
                    label="Login class"
                    placeholder="Login class"
                    component={FormSelect}
                    options={LOGIN_CLASSES_OPTIONS}
                    contextualHelpText="Defines classes of login"
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <Field
                    name="currency"
                    label="Currency"
                    placeholder="Currency"
                    component={FormInput}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Field
                    name="login_domain"
                    label="Login domain"
                    placeholder="Login domain"
                    component={FormSelect}
                    options={LOGIN_DOMAIN_OPTIONS}
                    contextualHelpText="Domain of the login information"
                  />
                </Grid>
                <Grid item xs={4}>
                  <Field
                    name="is_active"
                    label="Active"
                    placeholder="Active"
                    component={FormCheckbox}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <Field
                    name="profile_id"
                    label="Profile ID"
                    placeholder="Profile ID"
                    component={FormInput}
                  />
                </Grid>
              </Grid>

              <div>
                <DialogActions>
                  <Button color="secondary" size="small" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button disabled={!isValid || isFetching} size="small" type="submit">
                    Create
                  </Button>
                </DialogActions>
              </div>
            </StyledFrom>
          )}
        </Formik>
      </DialogContent>
      <Snackbar
        type={snackBarState.resultType}
        isOpenSnackbar={snackBarState.open}
        responseSuccessMessage={snackBarState.responseSuccessMessage}
        responseErrorMessage={snackBarState.responseSuccessErrorMessage}
        onClose={handleCloseSnackBar}
      />
    </Dialog>
  );
};

export default CreateURMAUserModal;
